// The browser's navigation IS the app's navigation: every place the reader can be has a URL,
// back/forward walk it, a reload lands exactly there, and a link copied from the old webapp
// (`/<team>/channels/<name>`, `/<team>/messages/@user`, `/<team>/pl/<postId>`) opens the same thing
// here. No library — the History API and one parser.

import * as Permalinks from '../core/permalinks';

export type Destination = 'threads' | 'activity' | 'saved' | 'drafts' | 'search' | 'settings';

export type Route =
  | { kind: 'home'; team: string | null }
  /** `/<team>/channels/<name>` — channel by its URL slug (also the D/G channel name). */
  | { kind: 'channel'; team: string; channelName: string; thread?: string | null }
  /** `/<team>/messages/@<username>` (DM) — resolved to the DM channel. */
  | { kind: 'dm'; team: string; username: string; thread?: string | null }
  /** `/<team>/pl/<postId>` — a message; the conversation opens ON it. */
  | { kind: 'permalink'; team: string; postId: string }
  /** `/<team>/threads[/<rootId>]`, `/<team>/activity`, `/<team>/saved`, `/<team>/drafts`, `/<team>/search?q=`,
   *  `/<team>/settings[/<section>]` (the account settings page). */
  | { kind: 'destination'; team: string; destination: Destination; param?: string | null; thread?: string | null };

const DESTINATIONS: Destination[] = ['threads', 'activity', 'saved', 'drafts', 'search', 'settings'];

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

export function parseRoute(pathname: string, search = ''): Route {
  const parts = pathname.split('/').filter(Boolean).map(safeDecode);
  const q = new URLSearchParams(search);
  const thread = q.get('thread');
  if (parts.length === 0) return { kind: 'home', team: null };
  const team = parts[0];
  if (parts.length === 1) return { kind: 'home', team };
  const section = parts[1];
  if (section === 'channels' && parts[2]) return { kind: 'channel', team, channelName: parts[2], thread };
  if (section === 'messages' && parts[2]) {
    if (parts[2].startsWith('@')) return { kind: 'dm', team, username: parts[2].slice(1), thread };
    return { kind: 'channel', team, channelName: parts[2], thread };
  }
  if (section === 'pl' && parts[2]) return { kind: 'permalink', team, postId: parts[2] };
  if ((DESTINATIONS as string[]).includes(section)) {
    const destination = section as Destination;
    const param = destination === 'search' ? q.get('q') : parts[2] ?? null;
    return { kind: 'destination', team, destination, param, thread };
  }
  return { kind: 'home', team };
}

export function routeToPath(route: Route): string {
  const enc = encodeURIComponent;
  switch (route.kind) {
    case 'home':
      return route.team ? `/${enc(route.team)}` : '/';
    case 'channel':
      return `/${enc(route.team)}/channels/${enc(route.channelName)}${route.thread ? `?thread=${enc(route.thread)}` : ''}`;
    case 'dm':
      return `/${enc(route.team)}/messages/@${enc(route.username)}${route.thread ? `?thread=${enc(route.thread)}` : ''}`;
    case 'permalink':
      return `/${enc(route.team)}/pl/${enc(route.postId)}`;
    case 'destination': {
      const params = new URLSearchParams();
      if (route.destination === 'search' && route.param) params.set('q', route.param);
      if (route.thread) params.set('thread', route.thread);
      const qs = params.toString() ? `?${params}` : '';
      if (route.destination === 'search') return `/${enc(route.team)}/search${qs}`;
      return `/${enc(route.team)}/${route.destination}${route.param ? `/${enc(route.param)}` : ''}${qs}`;
    }
  }
}

type Listener = (route: Route) => void;
const listeners = new Set<Listener>();

// In-app history position, so the back/forward arrows know when there is somewhere to go. Every
// entry this app pushes carries its index in `history.state`; the browser keeps it across reloads.
interface NavState {
  idx: number;
}
let index = 0;
let maxIndex = 0;
const historyListeners = new Set<() => void>();

function readIndex(): number {
  const st = history.state as NavState | null;
  return st && typeof st.idx === 'number' ? st.idx : 0;
}

if (typeof window !== 'undefined') {
  index = readIndex();
  maxIndex = Math.max(index, Number(sessionStorage.getItem('satori.navMax') ?? 0) || 0);
  if (!history.state) history.replaceState({ idx: 0 } satisfies NavState, '');
  window.addEventListener('popstate', () => {
    index = readIndex();
    for (const l of historyListeners) l();
  });
}

function rememberMax(): void {
  try {
    sessionStorage.setItem('satori.navMax', String(maxIndex));
  } catch {
    /* only the forward arrow's state is lost */
  }
}

export function historyPosition(): { canGoBack: boolean; canGoForward: boolean } {
  return { canGoBack: index > 0, canGoForward: index < maxIndex };
}

export function onHistoryChange(listener: () => void): () => void {
  historyListeners.add(listener);
  return () => historyListeners.delete(listener);
}

export function goBack(): void {
  if (index > 0) history.back();
}

export function goForward(): void {
  if (index < maxIndex) history.forward();
}

export function currentRoute(): Route {
  return parseRoute(location.pathname, location.search);
}

/** Push (a new place the reader went) or replace (a correction of where they already are). */
export function navigate(route: Route, options: { replace?: boolean } = {}): void {
  const path = routeToPath(route);
  // Development flags (?hud, ?server, ?memory) survive in-app navigation.
  const keep = new URLSearchParams(location.search);
  const target = new URL(path, location.origin);
  for (const k of ['hud', 'server', 'memory']) if (keep.has(k)) target.searchParams.set(k, keep.get(k) ?? '');
  const href = target.pathname + target.search;
  if (href === location.pathname + location.search) return;
  if (options.replace) history.replaceState({ idx: index } satisfies NavState, '', href);
  else {
    index += 1;
    maxIndex = index; // a new branch: everything "forward" of here is gone
    rememberMax();
    history.pushState({ idx: index } satisfies NavState, '', href);
  }
  for (const l of listeners) l(route);
  for (const l of historyListeners) l();
}

export function onRoute(listener: Listener): () => void {
  listeners.add(listener);
  const pop = () => listener(currentRoute());
  window.addEventListener('popstate', pop);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', pop);
  };
}

/** The permalink a "Copiar enlace" writes (shared/API.md §25.4) — the webapp's own shape. */
// Links to messages are built on the server's SiteURL (client config), like every native client
// and the web's own Forward: the server only previews a permalink to ITSELF, and the apps only
// recognise that base. The current origin is the fallback until the config is known — and the two
// are the same once the web is served on the chat domain.
let permalinkBase = '';

export function setPermalinkBase(siteUrl: string): void {
  permalinkBase = siteUrl.replace(/\/+$/, '');
}

export function permalinkUrl(teamName: string, postId: string): string {
  return `${permalinkBase || location.origin}${routeToPath({ kind: 'permalink', team: teamName, postId })}`;
}

/** The post a link points at when it is OUR message permalink (SiteURL or this origin), else null. */
export function permalinkPostId(url: string): string | null {
  for (const base of [permalinkBase, location.origin]) {
    if (!base) continue;
    const hit = Permalinks.intake(url, base);
    if (hit?.kind === 'target') return hit.postId;
  }
  return null;
}
